import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { loadErrorMessage } from "shared/loadOrError";
import { dataMapperSelection, dataMapperService } from "./dataMapperServices";
import { canUseDataMapper } from "./rbac";
import { bindSelectionToSignedInUser } from "./shared/editorSupport";
import {
  isRecordChanged,
  sameId,
  type DataMapperMappingConfig,
  type DataMapperProfile,
  type DataMapperProfilePatch,
  type DataMapperSchemaEntry,
} from "./dataMapper.types";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

/**
 * The step numbers HQ's screens save as `lastStep` (DATA_MAPPER_STEP_LABELS):
 * the editors as the standalone tool numbered them, Target and Import after its last.
 */
export const DATA_MAPPER_STEP = { schema: 3, map: 5, transform: 6, target: 9, import: 10 } as const;

/** How long the selection must sit still before it is saved to the profile. */
export const STATE_SAVE_DEBOUNCE_MS = 1500;

type Json = Record<string, unknown>;
const isObject = (v: unknown): v is Json => v != null && typeof v === "object" && !Array.isArray(v);

/**
 * The profile's saved UI state (`data-mapper-profiles` `state` + `lastStep`):
 * which step this profile was left on and which schema, mapping configuration
 * and mapping were open, so opening the profile again resumes there. The state
 * keeps the standalone tool's keys (`schema.activeSchemaId`,
 * `mapping.currentConfigId`, `transform.editingMappingId`), which the API's
 * profile clone rewrites and its list summarises.
 *
 * Saves are DEBOUNCED and MERGED: keys this screen does not own (a data source,
 * selected tables) are carried over from the state the API last returned. A
 * save sends `expectUpdatedAt`; on a 409 (someone saved the profile meanwhile)
 * it re-reads the profile, re-merges and saves once more, so their keys survive
 * and the selection still lands.
 *
 * Never saves before the profile's state has been READ in this visit: saving
 * the empty selection a freshly opened profile starts with would erase what it
 * was left on.
 */
class DataMapperProfileStateStore {
  rootStore: RootStore;

  /** The step the open editor is on (a number, not an entity). */
  lastStep: number | null = null;
  saving = false;
  /** The last save's failure; the editors show it quietly (nothing they hold is lost). */
  error: string | null = null;
  /** The profile whose saved state was read into the selection on this visit. */
  restoredProfileId: string | null = null;

  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable<this, "timer">(this, { rootStore: false, timer: false });
  }

  private get selection() {
    return dataMapperSelection(this.rootStore);
  }

  private get profiles() {
    return dataMapperService(this.rootStore, "data-mapper-profiles");
  }

  private cachedProfile(profileId: string): DataMapperProfile | null {
    const all = (this.profiles?.entities ?? []) as unknown as DataMapperProfile[];
    return all.find((p) => sameId(p.id, profileId)) ?? null;
  }

  /** The open profile's record, looked up in the profiles ServiceStore (for the headers' label). */
  get openProfile(): DataMapperProfile | null {
    const id = this.selection?.selectedProfileId;
    return id ? this.cachedProfile(id) : null;
  }

  /** An editor route was entered: read the profile's saved state (once per visit), then save the step. */
  async enterStep(step: number): Promise<void> {
    // A selection another signed-in user made in this tab is dropped before anything is read.
    bindSelectionToSignedInUser(this.rootStore);
    if (!canUseDataMapper(this.rootStore)) return;
    this.lastStep = step;
    // restore() swallows a sign-out: no save scheduled for the next person.
    const ended = sessionMark(this.rootStore);
    await this.restore();
    if (ended()) return;
    this.schedule();
  }

  /**
   * Read the open profile's saved state and adopt the ids it names, for those
   * this tab has not chosen. Once per profile per visit.
   */
  async restore(): Promise<void> {
    const profileId = this.selection?.selectedProfileId ?? null;
    if (!profileId || sameId(profileId, this.restoredProfileId) || !this.profiles) return;
    try {
      // `get` answers `state` (find does not); the store caches it.
      const profile = (await this.profiles.get(profileId)) as unknown as DataMapperProfile | undefined;
      const state = isObject(profile?.state) ? profile.state : {};
      const schema = isObject(state.schema) ? state.schema : {};
      const mapping = isObject(state.mapping) ? state.mapping : {};
      const transform = isObject(state.transform) ? state.transform : {};
      const id = (v: unknown) => (typeof v === "string" && v ? v : null);
      runInAction(() => {
        if (!sameId(this.selection?.selectedProfileId, profileId)) return;
        this.selection?.adoptSavedSelection({
          schemaId: id(schema.activeSchemaId),
          mappingConfigId: id(mapping.currentConfigId),
          mappingId: id(transform.editingMappingId),
        });
        this.restoredProfileId = profileId;
        this.error = null;
      });
    } catch (err) {
      // Not read: this visit never saves (it could erase what the profile was left on).
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = loadErrorMessage(err, "The profile's saved progress could not be read.");
      });
    }
  }

  /** Save the selection and step after it has sat still for STATE_SAVE_DEBOUNCE_MS. */
  schedule() {
    const profileId = this.selection?.selectedProfileId ?? null;
    if (!profileId || !sameId(profileId, this.restoredProfileId)) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.save();
    }, STATE_SAVE_DEBOUNCE_MS);
  }

  /** Save now if a save is waiting (route exit), so the last change is not dropped. */
  async flush(): Promise<void> {
    if (!this.timer) return;
    clearTimeout(this.timer);
    this.timer = null;
    await this.save();
  }

  /** The state to save: the last saved state with this screen's keys over it. */
  buildState(previous: unknown): Json {
    const selection = this.selection;
    const base: Json = isObject(previous) ? { ...previous } : {};
    const schemaId = selection?.selectedSchemaId ?? null;
    const configId = selection?.selectedMappingConfigId ?? null;
    const schemaRows = (dataMapperService(this.rootStore, "data-mapper-schemas")?.entities ??
      []) as unknown as DataMapperSchemaEntry[];
    const configRows = (dataMapperService(this.rootStore, "data-mapper-mapping-configs")?.entities ??
      []) as unknown as DataMapperMappingConfig[];
    const schemaEntry = schemaId ? schemaRows.find((s) => sameId(s.id, schemaId)) : undefined;
    const config = configId ? configRows.find((c) => sameId(c.id, configId)) : undefined;
    const rawCode = isObject(schemaEntry?.schema) ? schemaEntry.schema.rawCode : undefined;

    const prevSchema: Json = isObject(base.schema) ? { ...base.schema } : {};
    // The Profiles list names the schema from rawCode (summariseState): it must be the chosen
    // schema's, or nothing. The previous one's is kept only while the same schema is chosen
    // and its row is not loaded (Codex r1).
    if (typeof rawCode === "string") prevSchema.rawCode = rawCode;
    else if (!schemaId || !sameId(prevSchema.activeSchemaId, schemaId)) delete prevSchema.rawCode;
    base.schema = { ...prevSchema, activeSchemaId: schemaId };
    const prevMapping: Json = isObject(base.mapping) ? { ...base.mapping } : {};
    // The standalone tool's unsaved working copy: HQ keeps none server-side, and a
    // copy for another config would be restored over this one by an older client.
    delete prevMapping.mappings;
    base.mapping = {
      ...prevMapping,
      currentConfigId: configId,
      currentConfigName: config?.name ?? (configId ? (prevMapping.currentConfigName ?? "") : ""),
    };
    const prevTransform = isObject(base.transform) ? base.transform : {};
    base.transform = { ...prevTransform, editingMappingId: selection?.selectedMappingId ?? null };
    if (this.lastStep != null) base.lastStep = this.lastStep;
    base.savedAt = new Date().toISOString();
    base.profileId = selection?.selectedProfileId ?? null;
    return base;
  }

  private async save(): Promise<void> {
    const profileId = this.selection?.selectedProfileId ?? null;
    const service = this.profiles;
    if (!profileId || !service || !sameId(profileId, this.restoredProfileId)) return;
    this.saving = true;
    const attempt = async () => {
      // A `find` (the Profiles list) replaces the cached profile with one that has no `state`:
      // read it again rather than merge over nothing and erase the keys this screen does not own.
      if (!("state" in (this.cachedProfile(profileId) ?? {}))) await service.get(profileId);
      // The tab may have opened another profile meanwhile: never write its keys into this one (Codex r2).
      if (!sameId(this.selection?.selectedProfileId, profileId)) return;
      const cached = this.cachedProfile(profileId);
      const patch: DataMapperProfilePatch = { state: this.buildState(cached?.state) };
      if (this.lastStep != null) patch.lastStep = this.lastStep;
      if (cached?.updatedAt) patch.expectUpdatedAt = cached.updatedAt;
      await service.patch(profileId, patch as never);
    };
    try {
      try {
        await attempt();
      } catch (err) {
        if (isSessionEnded(err) || !isRecordChanged(err)) throw err;
        // Someone saved the profile meanwhile: take theirs and put this tab's keys over it, once.
        // Only this screen's keys (where it was left: step, schema, config, mapping) are
        // last-write-wins by design; every other key is theirs, merged. Never for a profile
        // this tab has since left.
        await service.get(profileId);
        if (!sameId(this.selection?.selectedProfileId, profileId)) return;
        await attempt();
      }
      runInAction(() => {
        this.error = null;
      });
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = loadErrorMessage(err, "This profile's progress could not be saved.");
      });
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  /** The notice's Retry: read the profile again if that is what failed, then save now. */
  async retry(): Promise<void> {
    const profileId = this.selection?.selectedProfileId ?? null;
    if (!profileId) return;
    if (!sameId(profileId, this.restoredProfileId)) await this.restore();
    if (!sameId(profileId, this.restoredProfileId)) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    await this.save();
  }

  /** Forget what was read, and any pending save (a profile was opened afresh). Route exits call `flush`. */
  reset() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.restoredProfileId = null;
    this.lastStep = null;
    this.error = null;
  }

  /**
   * Everything reset() keeps across this wizard's route changes goes too: on
   * sign-out it was the previous person's. Called by RootStore.purgeUserData,
   * which prefers this to reset().
   */
  resetForSignOut() {
    this.reset();
    this.saving = false;
  }
}

export default DataMapperProfileStateStore;
